import { create } from 'zustand';
import { Appointment } from 'src/models';
import { logger } from 'src/services';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { getFixedT } from 'i18next';
import { i18n, Language } from 'src/services/i18n';
import {
  logoutApi,
  validateLoggedInUserApi,
  validateAnonymousUserApi,
  ValidateLoggedInUserApiPayload,
  ValidateAnonymousUserApiPayload,
} from 'src/api';

type State = {
  token: string | null;
  appointment: Appointment | null;
  error: string | null;
  loading: boolean;
  showConditionSelector: boolean;
};

type Actions = {
  logout: (payload: { chatToken: string }) => void;
  validateLoggedInUser: (payload: ValidateLoggedInUserApiPayload) => void;
  validateAnonymousUser: (
    payload: ValidateAnonymousUserApiPayload & {
      skipGenericCheck?: boolean;
    }
  ) => void;
};

export const useAuthStore = create<State & Actions>((set) => ({
  token: null,
  appointment: null,
  loading: true,
  error: null,
  showConditionSelector: false,
  logout: async ({ chatToken }) => {
    try {
      await logoutApi({ chatToken });
      set({ token: null, appointment: null });
    } catch (error) {
      toast.error(getFixedT(null, 'auth')('errors.logoutFailed'));
      logger.error('Error logging out:', {}, error as Error);
    }
  },
  validateLoggedInUser: async ({ chatToken }) => {
    set({ error: null, loading: true });
    try {
      const response = await validateLoggedInUserApi({ chatToken });
      const appointment = response.data;
      set({ appointment, loading: false });
    } catch (error) {
      logger.error(
        'Failed to validate logged in user',
        { chatToken },
        error as Error
      );
      set({
        error: getFixedT(null, 'auth')('errors.userValidationFailed'),
        loading: false,
      });
    }
  },
  validateAnonymousUser: async ({
    skipGenericCheck,
    chatToken,
    preferredLanguage,
    ...rest
  }) => {
    set({ error: null });
    try {
      const response = await validateAnonymousUserApi({
        ...rest,
        chatToken,
        preferredLanguage,
      });
      const appointment = response.data;
      const { user_access_token, patient_preferred_language } = appointment;
      set({
        appointment,
        token: user_access_token,
        showConditionSelector:
          !skipGenericCheck && appointment.condition_type === 'generic',
      });
      i18n.changeLanguage(patient_preferred_language as Language, {
        persist: true,
      });
    } catch (error) {
      set({
        error: getFixedT(null, 'auth')('patientAuth.dob.validationError'),
      });
    }
  },
}));
